import { Box, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "$utils/useSnackbar";
import Settings3LineIcon from "remixicon-react/Settings3LineIcon";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";
import { Company } from "$types";
import { ButtonProps } from "$components/Button";
import { BtnPoper, Button } from "$components";
import useStyles from "./styles/CatalogueActions.styles";
import JImportModal from "$components/JImportModal/JImportModal";
import { ImportType } from "$types/import/ImportType";
import useExport from "$hooks/useExport";
import useAuth from "$hooks/useAuth";
import { useSocket } from "$utils/socket";
import { CacheInvalidationAction } from "$utils/cache";
import { Switch } from "$components/Forms/Switch";

const validateLabels = {
  [ImportType.PRODUCT]: "Ajouter",
  [ImportType.PRODUCT_UPDATE]: "Editer",
  [ImportType.PRODUCT_DELETE]: "Supprimer",
};

const pastParticiples = {
  [ImportType.PRODUCT]: "ajouté",
  [ImportType.PRODUCT_UPDATE]: "modifié",
  [ImportType.PRODUCT_DELETE]: "supprimé",
};

type Props = {
  company?: Company;
  handleExportCatalog: (type: ImportType) => void;
  handlePushCategory: () => void;
  toggleOpen: (newValue: boolean) => void;
  isLoadingExport?: boolean;
  filterIsActive: boolean;
  setFilterIsActive: (value: boolean) => void;
};

const CatalogueActions = ({
  company,
  handleExportCatalog,
  handlePushCategory,
  toggleOpen,
  isLoadingExport,
  filterIsActive,
  setFilterIsActive,
}: Props) => {
  const [showModalImport, setShowModalImport] = useState<boolean>(false);
  const [importType, setImportType] = useState<ImportType>(undefined);
  const { exportXLSTemplate } = useExport();
  const socket = useSocket();
  const { token, isAdmin } = useAuth();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);

  const handleExportTemplate = async () => {
    if (importType === ImportType.PRODUCT_UPDATE) {
      handleExportCatalog(importType);
    } else {
      await exportXLSTemplate(
        token,
        importType,
        `modèle-produits${
          importType === ImportType.PRODUCT_DELETE ? "-supression" : ""
        }`
      );
    }
  };

  const onImportFinish = async () => {
    // invalidate stock and categories queries
    socket.emit("invalidateQueries", [
      CacheInvalidationAction.GET_CATEGORIES_BY_COMPANY_ID,
      CacheInvalidationAction.GET_PRODUCT_STOCK,
    ]);
  };

  const openModalImport = (type: ImportType) => {
    setShowModalImport(true);
    setImportType(type);
  };

  const classes = useStyles();
  const { _id, isActive } = company || {};
  const areButtonsDisabled = !_id || !isActive;
  const defaultProps: ButtonProps = {
    disabled: areButtonsDisabled,
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterIsActive(e.target.checked);
  };

  return (
    <>
      <Switch
        label="Produits actifs"
        onChange={handleSwitchChange}
        checked={filterIsActive}
      />
      {isAdmin() && (
        <>
          <Button
            variant="outlined"
            {...defaultProps}
            onClick={handlePushCategory}
            startIcon={<Settings3LineIcon widths={16} />}
          >
            Gérer les catégories
          </Button>
          <Button
            variant="outlined"
            {...defaultProps}
            onClick={() => {
              toggleOpen(true);
            }}
            startIcon={<AddIcon />}
          >
            Ajouter un produit
          </Button>
        </>
      )}
      <Button
        variant="outlined"
        {...defaultProps}
        onClick={() => handleExportCatalog(ImportType.PRODUCT)}
        disabled={isLoadingExport || areButtonsDisabled}
        startIcon={
          isLoadingExport ? <CircularProgress size={20} /> : <Upload2LineIcon />
        }
      >
        Exporter le catalogue
      </Button>
      {isAdmin() && (
        <>
          <BtnPoper
            BtnLabel="Opérations en masse"
            BtnComponent={Button}
            BtnProps={{
              variant: "outlined",
              disabled: areButtonsDisabled,
            }}
            BtnIcon={ExpandMoreIcon}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: "300px",
              }}
            >
              <Button
                onClick={() => openModalImport(ImportType.PRODUCT)}
                variant="contained"
                className={classes.BtnOnPopper}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 0,
                  color: "black !important",
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  padding: "6px",
                  minWidth: "300px",
                  "&:hover": {
                    backgroundColor: "#e0e0e0 !important",
                    color: "black !important",
                    border: "none !important",
                  },
                  "&.MuiButton-text:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "&.MuiButton-root:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "& svg": {
                    color: "black !important",
                    marginRight: "8px",
                  },
                  "& .MuiButton-label": {
                    color: "black !important",
                  },
                  "& span": {
                    color: "black !important",
                  },
                }}
              >
                <Download2LineIcon />
                Importer des produits
              </Button>
              <Button
                onClick={() => openModalImport(ImportType.PRODUCT_UPDATE)}
                variant="contained"
                className={classes.BtnOnPopper}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 0,
                  color: "black !important",
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  padding: "6px",
                  minWidth: "300px",
                  "&:hover": {
                    backgroundColor: "#e0e0e0 !important",
                    color: "black !important",
                    border: "none !important",
                  },
                  "&.MuiButton-text:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "&.MuiButton-root:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "& svg": {
                    color: "black !important",
                    marginRight: "8px",
                  },
                  "& .MuiButton-label": {
                    color: "black !important",
                  },
                  "& span": {
                    color: "black !important",
                  },
                }}
              >
                <Edit2LineIcon />
                Mettre à jour des produits
              </Button>
              <Button
                onClick={() => openModalImport(ImportType.PRODUCT_DELETE)}
                variant="contained"
                className={classes.BtnOnPopper}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 0,
                  color: "black !important",
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  padding: "6px",
                  minWidth: "300px",
                  "&:hover": {
                    backgroundColor: "#e0e0e0 !important",
                    color: "black !important",
                    border: "none !important",
                  },
                  "&.MuiButton-text:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "&.MuiButton-root:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "& svg": {
                    color: "black !important",
                    marginRight: "8px",
                  },
                  "& .MuiButton-label": {
                    color: "black !important",
                  },
                  "& span": {
                    color: "black !important",
                  },
                }}
              >
                <DeleteBin2LineIcon />
                Supprimer des produits
              </Button>
            </Box>
          </BtnPoper>
          <JImportModal
            contextId={_id}
            canExportData={importType === ImportType.PRODUCT_UPDATE}
            dataName="produits"
            customDownload={handleExportTemplate}
            showImportModal={!!showModalImport}
            setShowImportModal={setShowModalImport}
            type={importType}
            onImportFinish={onImportFinish}
            openSnackbar={openSnackbar}
            validationLabel={validateLabels[importType]}
            pastParticiple={pastParticiples[importType]}
          />
        </>
      )}
    </>
  );
};

export default CatalogueActions;
